/* global document */

import {
    REPORT_RECORD_COUNT_ELEMENT_ID,
    REPORT_RECORD_COUNT_FIELD_WRAP_ID,
} from '../../../agreements/report-record-count.constants';
import type {
    ReportFilterFieldSpec,
    ReportFilterSelectOption,
    ReportingTargetFilterDefinition,
} from '../reporting-target-filter.types';
import { createReportMultiselectElement } from './report-multiselect.widget';

/** Mount for {@link ReportingTargetFilterDefinition.commonFilterFields} — must exist in `taskpane.html`. */
export const REPORT_COMMON_FILTERS_MOUNT_ID = 'report-common-filters';

/** Mount for {@link ReportingTargetFilterDefinition.targetFilterFields} (excluding leading Record Count) — must exist in `taskpane.html`. */
export const REPORT_TARGET_FILTERS_MOUNT_ID = 'report-target-filters';

function clearMount(mount: HTMLElement): void {
    mount.replaceChildren();
}

/** Native `type="date"` has no `::placeholder`; match text placeholder look when empty. */
function wireDateInputPlaceholderStyle(input: HTMLInputElement): void {
    function sync(): void {
        input.classList.toggle('field-input--date-unfilled', !input.value);
    }
    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
    sync();
}

function createLabel(spec: ReportFilterFieldSpec): HTMLLabelElement {
    const label = document.createElement('label');
    label.className = 'field-label';
    label.htmlFor = spec.elementId;
    label.append(document.createTextNode(spec.label));
    if (spec.optional) {
        label.append(document.createTextNode(' '));
        const optionalMark = document.createElement('span');
        optionalMark.className = 'field-label__optional';
        optionalMark.textContent = '(optional)';
        label.append(optionalMark);
    }
    return label;
}

function createTextNumberOrDateInput(
    spec: ReportFilterFieldSpec,
    dynamicDateDefaults?: Readonly<Record<string, string>>
): HTMLInputElement {
    const input = document.createElement('input');
    input.id = spec.elementId;
    input.className = 'field-input';
    if (spec.kind === 'number') {
        input.type = 'number';
        if (typeof spec.min === 'number') {
            input.min = String(spec.min);
        }
        if (spec.defaultValue !== undefined) {
            input.value = spec.defaultValue;
        }
    } else if (spec.kind === 'date') {
        input.type = 'date';
        const dateInitial = dynamicDateDefaults?.[spec.elementId] ?? spec.defaultValue;
        if (dateInitial !== undefined && dateInitial !== '') {
            input.value = dateInitial;
        }
        wireDateInputPlaceholderStyle(input);
    } else {
        input.type = 'text';
        if (spec.defaultValue !== undefined) {
            input.value = spec.defaultValue;
        }
    }
    if (spec.placeholder !== undefined && spec.kind !== 'date') {
        input.placeholder = spec.placeholder;
    }
    return input;
}

function resolveFieldOptions(
    spec: ReportFilterFieldSpec,
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>
): readonly ReportFilterSelectOption[] {
    const fromDynamic = dynamicSelectOptions?.[spec.elementId];
    if (fromDynamic !== undefined) {
        return fromDynamic;
    }
    return spec.options ?? [];
}

function createSelectControl(
    spec: ReportFilterFieldSpec,
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>
): HTMLSelectElement {
    const select = document.createElement('select');
    select.id = spec.elementId;
    select.className = 'field-input';
    const options = resolveFieldOptions(spec, dynamicSelectOptions);

    if (spec.optional) {
        const placeholderOpt = document.createElement('option');
        placeholderOpt.value = '';
        placeholderOpt.textContent = spec.placeholder ?? 'Select…';
        select.append(placeholderOpt);
    }

    for (const opt of options) {
        const optionEl = document.createElement('option');
        optionEl.value = opt.value;
        optionEl.textContent = opt.label;
        select.append(optionEl);
    }

    if (spec.defaultValue !== undefined) {
        select.value = spec.defaultValue;
    }

    return select;
}

function createControlElement(
    spec: ReportFilterFieldSpec,
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>,
    dynamicDateDefaults?: Readonly<Record<string, string>>
): HTMLElement {
    if (spec.kind === 'multiselect') {
        const options = resolveFieldOptions(spec, dynamicSelectOptions);
        return createReportMultiselectElement({ ...spec, options });
    }
    if (spec.kind === 'select') {
        return createSelectControl(spec, dynamicSelectOptions);
    }
    return createTextNumberOrDateInput(spec, dynamicDateDefaults);
}

function createFieldColumn(
    spec: ReportFilterFieldSpec,
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>,
    dynamicDateDefaults?: Readonly<Record<string, string>>
): HTMLElement {
    const col = document.createElement('div');
    col.className = 'filters-grid__field';
    const label = createLabel(spec);
    if (spec.kind === 'multiselect') {
        label.htmlFor = `${spec.elementId}-trigger`;
    }
    col.append(label, createControlElement(spec, dynamicSelectOptions, dynamicDateDefaults));
    if (spec.helperText !== undefined && spec.helperText !== '') {
        const hint = document.createElement('p');
        hint.className = 'hint';
        hint.id = `${spec.elementId}-helper`;
        hint.textContent = spec.helperText;
        col.append(hint);
    }
    return col;
}

/**
 * Appends field rows to `container` (does not clear). Honors {@link ReportFilterFieldSpec.rowGroup} for split rows.
 */
function appendFieldRows(
    container: HTMLElement,
    fields: readonly ReportFilterFieldSpec[],
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>,
    dynamicDateDefaults?: Readonly<Record<string, string>>
): void {
    let i = 0;
    while (i < fields.length) {
        const spec = fields[i];
        if (spec.rowGroup !== undefined) {
            const groupId = spec.rowGroup;
            const group: ReportFilterFieldSpec[] = [];
            while (i < fields.length && fields[i].rowGroup === groupId) {
                group.push(fields[i]);
                i++;
            }
            if (group.length >= 2) {
                const row = document.createElement('div');
                row.className = 'filters-grid__row filters-grid__row--split';
                row.append(
                    createFieldColumn(group[0], dynamicSelectOptions, dynamicDateDefaults),
                    createFieldColumn(group[1], dynamicSelectOptions, dynamicDateDefaults)
                );
                container.append(row);
                for (let j = 2; j < group.length; j++) {
                    container.append(
                        createFieldColumn(group[j], dynamicSelectOptions, dynamicDateDefaults)
                    );
                }
            } else if (group.length === 1) {
                container.append(
                    createFieldColumn(group[0], dynamicSelectOptions, dynamicDateDefaults)
                );
            }
        } else {
            container.append(createFieldColumn(spec, dynamicSelectOptions, dynamicDateDefaults));
            i++;
        }
    }
}

function renderFieldsInto(
    mount: HTMLElement,
    fields: readonly ReportFilterFieldSpec[],
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>,
    dynamicDateDefaults?: Readonly<Record<string, string>>
): void {
    clearMount(mount);
    appendFieldRows(mount, fields, dynamicSelectOptions, dynamicDateDefaults);
}

/**
 * Builds filter UI from a reporting-target definition. Call when the user changes target (and once on load).
 *
 * @param dynamicSelectOptions Optional per-`elementId` options for `select` / `multiselect` fields (e.g. facility codes from Get-UserInfo).
 * @param dynamicDateDefaults Optional per-`elementId` initial values for `date` inputs (e.g. Material Summary to-date defaults to today).
 */
export function renderReportingTargetPanels(
    definition: ReportingTargetFilterDefinition,
    dynamicSelectOptions?: Readonly<Record<string, readonly ReportFilterSelectOption[]>>,
    dynamicDateDefaults?: Readonly<Record<string, string>>
): void {
    const commonMount = document.getElementById(REPORT_COMMON_FILTERS_MOUNT_ID);
    const targetMount = document.getElementById(REPORT_TARGET_FILTERS_MOUNT_ID);
    const recordCountWrap = document.getElementById(REPORT_RECORD_COUNT_FIELD_WRAP_ID);
    if (!commonMount || !targetMount) {
        return;
    }

    renderFieldsInto(
        commonMount,
        definition.commonFilterFields,
        dynamicSelectOptions,
        dynamicDateDefaults
    );
    clearMount(targetMount);

    targetMount.setAttribute('aria-label', `${definition.displayName} filters`);

    const fields = definition.targetFilterFields;
    const hasLeadingRecordCount =
        fields.length > 0 && fields[0].elementId === REPORT_RECORD_COUNT_ELEMENT_ID;
    const targetFieldsOnly = hasLeadingRecordCount ? fields.slice(1) : fields;

    if (recordCountWrap) {
        if (hasLeadingRecordCount && fields[0] !== undefined) {
            clearMount(recordCountWrap);
            recordCountWrap.append(
                createFieldColumn(fields[0], dynamicSelectOptions, dynamicDateDefaults)
            );
            recordCountWrap.classList.remove('hidden');
        } else {
            clearMount(recordCountWrap);
            recordCountWrap.classList.add('hidden');
        }
    }

    if (targetFieldsOnly.length === 0) {
        if (definition.emptyTargetFiltersMessage) {
            const message = document.createElement('p');
            message.className = 'hint';
            message.textContent = definition.emptyTargetFiltersMessage;
            targetMount.append(message);
        }
        return;
    }

    appendFieldRows(targetMount, targetFieldsOnly, dynamicSelectOptions, dynamicDateDefaults);
}
